import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
      {/* Enhanced Background with Glassmorphism */}
      <div className="fixed inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 backdrop-blur-3xl"></div>

      {/* Animated Background Orbs */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-purple-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute top-1/4 -right-40 w-96 h-96 bg-blue-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-pink-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-pulse animation-delay-4000"></div>
      </div>

      {/* Dot pattern overlay */}
      <div className="fixed inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%2523ffffff%22%20fill-opacity%3D%220.03%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

      {/* Navbar */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-3">
        <div className="flex items-center gap-3">
          {/* Logo */}
          <div className="relative cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-10 bg-white/20 rounded border border-white/30 flex items-center justify-center backdrop-blur-xl">
              <div className="relative">
                <div className="w-6 h-8 bg-blue-300 rounded-[2px] flex items-center justify-center">
                  <span className="text-white font-semibold text-xs">5</span>
                  <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                  <div className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
            <div className="absolute -right-1 -bottom-1 w-8 h-10 bg-white/20 rounded border border-white/30 flex items-center justify-center -rotate-6 backdrop-blur-xl">
              <div className="relative">
                <div className="w-6 h-8 bg-gray-800 rounded-[2px] flex items-center justify-center">
                  <span className="text-white font-semibold text-2xl">1</span>
                  <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                  <div className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          <span className="text-xl font-medium text-white ml-6">Scrumo</span>
        </div>

        {/* Nav Links */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/blog')} className="px-4 py-2 text-sm text-white rounded-full border border-white/30 hover:bg-white/20 transition-colors">
            Blog
          </button>
          <button onClick={() => navigate('/faq')} className="px-4 py-2 text-sm text-white rounded-full border border-white/30 hover:bg-white/20 transition-colors">
            FAQ
          </button>
          <button onClick={() => navigate('/create')} className="px-4 py-2 text-sm text-white rounded-full border border-white/30 hover:bg-white/20 transition-colors">
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6">
        {/* Floating Elements */}
        <div className="absolute top-32 left-20 animate-float hidden lg:block">
          <div className="w-12 h-15 bg-white/10 backdrop-blur-xl rounded-lg border border-white/20 flex items-center justify-center shadow-2xl rotate-12 hover:rotate-0 transition-transform duration-500">
            <span className="text-white font-bold text-lg">3</span>
          </div>
        </div>

        <div className="absolute top-48 right-32 animate-float animation-delay-2000 hidden lg:block">
          <div className="w-12 h-15 bg-white/10 backdrop-blur-xl rounded-lg border border-white/20 flex items-center justify-center shadow-2xl -rotate-12 hover:rotate-0 transition-transform duration-500">
            <span className="text-white font-bold text-lg">8</span>
          </div>
        </div>

        <div className="absolute bottom-48 left-40 animate-float animation-delay-4000 hidden lg:block">
          <div className="w-12 h-15 bg-white/10 backdrop-blur-xl rounded-lg border border-white/20 flex items-center justify-center shadow-2xl rotate-6 hover:rotate-0 transition-transform duration-500">
            <span className="text-white font-bold text-lg">13</span>
          </div>
        </div>

        {/* Hero Content */}
        <div className="text-center max-w-4xl mx-auto">
          {/* Status Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 mb-8">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-purple-200">Trusted by 10,000+ Teams</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Planning Poker
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400">
              Made Simple
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-lg md:text-xl text-purple-200 mb-12 max-w-2xl mx-auto leading-relaxed">
            Streamline your agile estimation process with our intuitive platform.
            Collaborate seamlessly and reach consensus faster.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button
              onClick={() => navigate('/create')}
              className="group px-8 py-4 text-lg bg-gradient-to-r from-purple-500 to-blue-500 text-white border-0 hover:from-purple-600 hover:to-blue-600 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              Start Planning Now
              <svg className="ml-2 w-5 h-5 inline group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Button>
            <Button
              onClick={() => navigate('/join')}
              className="px-8 py-4 text-lg bg-white/10 backdrop-blur-xl text-white border border-white/30 hover:bg-white/20 hover:scale-105 transition-all duration-300"
            >
              Join Room
            </Button>
          </div>

          {/* Scroll Indicator */}
          <div className="flex justify-center">
            <div className="animate-bounce">
              <svg className="w-6 h-6 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Choose Scrumo?</h2>
            <p className="text-purple-200 text-lg max-w-2xl mx-auto">Built for modern agile teams who value simplicity and efficiency</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-blue-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Real-time Collaboration</h3>
              <p className="text-purple-200 leading-relaxed">See votes as they happen. Foster better discussions with your team in real-time.</p>
            </div>

            <div className="group bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-400 to-purple-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Instant Analytics</h3>
              <p className="text-purple-200 leading-relaxed">Get insights into your team's estimation patterns and improve accuracy over time.</p>
            </div>

            <div className="group bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-purple-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Lightning Fast</h3>
              <p className="text-purple-200 leading-relaxed">No sign-ups, no delays. Create a room and start estimating in seconds.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-purple-200 text-lg max-w-2xl mx-auto">Get started in three simple steps</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Create Room</h3>
              <p className="text-purple-200">Start a new planning session instantly. No registration required.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Invite Team</h3>
              <p className="text-purple-200">Share the room link with your team members to join the session.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Start Estimating</h3>
              <p className="text-purple-200">Vote on user stories and reach consensus faster than ever.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Transform Your Planning?</h2>
            <p className="text-purple-200 text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of agile teams who have already streamlined their estimation process with Scrumo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/create')}
                className="group px-8 py-4 text-lg bg-gradient-to-r from-purple-500 to-blue-500 text-white border-0 hover:from-purple-600 hover:to-blue-600 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                Start Free Session
                <svg className="ml-2 w-5 h-5 inline group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Button>
              <Button
                onClick={() => navigate('/join')}
                className="px-8 py-4 text-lg bg-white/10 backdrop-blur-xl text-white border border-white/30 hover:bg-white/20 hover:scale-105 transition-all duration-300"
              >
                Join Room
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-3 mb-4 md:mb-0">
              <div className="w-8 h-10 bg-white/15 rounded-lg border border-white/20 flex items-center justify-center backdrop-blur-xl">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <span className="text-xl font-semibold text-white">Scrumo</span>
            </div>
            <div className="flex gap-6">
              <button onClick={() => navigate('/blog')} className="text-white/80 hover:text-white transition-colors">
                Blog
              </button>
              <button onClick={() => navigate('/faq')} className="text-white/80 hover:text-white transition-colors">
                FAQ
              </button>
              <span className="text-purple-200">© 2024 Scrumo</span>
            </div>
          </div>
        </div>
      </footer>
   </div>
  );
};

export default Landing;
